import "vue2-animate/dist/vue2-animate.min.css";
import Vue from "vue";
import VueI18n from "vue-i18n";
import Nprogress from "nprogress";
import VeeValidate from "vee-validate";
import Notifications from "vue-notification";
import velocity from "velocity-animate";
import moment from "moment";
import VueMatchHeights from "vue-match-heights";
import App from "./App.vue";
import { store } from "./store/store";
import router from "./router/index.js";
import GlobalComponents from "./globalComponents";
import GlobalFilters from "@/globalFilters";

import vuetify from "./plugins/vuetify";

import messages from "./locale";
import validationMessagesPl from "vee-validate/dist/locale/pl";
import validationMessagesEn from "vee-validate/dist/locale/en";

import VueScrollTo from "vue-scrollto";
import "leaflet/dist/leaflet.css";
import { vueGtmConfig } from "./plugins/vue-gtm";

import VueGtm from "vue-gtm";
import { permissionCheck } from "@/helpers/permission-check";
import { dev__warnNotify, storeSourceFromQuery } from "@/helpers/helpers";
import { Permission } from "@/enums/permission";

import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";
import posthog from "posthog-js";
import { moduleCheck } from "@/helpers/module-check";
import { FEATURE_FLAG } from "@/helpers/posthog";
import { AccountType } from "@/enums/account-type";
import blockEvents from "@/helpers/block-events-check";
import registerAnalyticsGroup from "@/helpers/register-analytics-group";
import usersService from "@/services/users";
import serviceHandler from "@/helpers/handlers/service";
import { setCustomValidators } from "@/config/validators";

import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";
import mixpanel from "mixpanel-browser";
import { initHotjar, stopHotjar } from "@/helpers/hotjar";

import { LOCAL_STORAGE_KEYS } from "@/enums/local-storage";
import axios from "axios";
import { ensureCurrentSession, getSessionId } from "@/helpers/auth-session";
// Near entry of your product, init Mixpanel
mixpanel.init(
  process.env.VUE_APP_MIXPANEL_APP_ID,
  {
    // persistence: "localStorage"
    api_host:
      process.env.VUE_APP_MIXPANEL_API_HOST || "https://api-eu.mixpanel.com",
    api_payload_format: "json"
  },
  "core"
);

mixpanel.init(
  process.env.VUE_APP_FREE_TOOL_MIXPANEL_APP_ID,
  {
    // persistence: "localStorage"
    api_host:
      process.env.VUE_APP_MIXPANEL_API_HOST || "https://api-eu.mixpanel.com",
    api_payload_format: "json"
  },
  "free_tool"
);

const setLanguage = async () => {
  if (!store.getters["auth/isAuthenticated"]) {
    return;
  }
  const { language, company_subscription } = await serviceHandler(
    usersService.getUserDetails()
  );
  store.dispatch("auth/setPlanRestrictions", company_subscription);
  store.dispatch("settings/setLanguage", language);
};

const checkWizardCompleted = async next => {
  if (!store.getters["company/isWizardCompleted"]) {
    next({ name: "wizard" });
    return false;
  }
  return true;
};

const getCompanyIdFromQuery = query => {
  if (!query || !query.companyId) return null;
  const id = +query.companyId;
  return Number.isNaN(id) ? null : id;
};

const fetchCompanies = async () => {
  const sessionId = getSessionId();
  store.commit("company/SET_LOADING", true);
  try {
    await store.dispatch("company/fetchCompanies");
    ensureCurrentSession(sessionId);
  } finally {
    if (sessionId === getSessionId()) {
      store.commit("company/SET_LOADING", false);
    }
  }
};

const setAnalytics = async () => {
  document.cookie = "__ca__chat" + "=; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
  document.cookie = "_ueuuid" + "=; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
  if (!store.state.company.company) {
    return;
  }
  const { user_id } = store.state.company.company.company_user;
  Sentry.setUser({ id: user_id });

  if (blockEvents()) {
    return;
  }
  const { id: company_id } = store.state.company.company;
  mixpanel.core.alias(user_id, mixpanel.core.get_distinct_id());
  mixpanel.core.identify(user_id);
  mixpanel.core.people.set({
    company_id: company_id,
    $name: store.state.auth.user.email,
    $email: store.state.auth.user.email
  });

  registerAnalyticsGroup(store.state.company);

  return;
};

Vue.config.productionTip = false;

Vue.prototype.$permission = Permission;
Vue.prototype.$hasAccess = permission => permissionCheck(permission);
Vue.prototype.$setUserProperties = params => {
  if (blockEvents()) return;
  if (!window.gtag) return;
  window.gtag("set", "user_properties", params);
};
router.beforeEach(async (to, from, next) => {
  const sessionId = getSessionId();
  const isAuthenticated = store.getters["auth/isAuthenticated"];
  Nprogress.start();

  posthog.reloadFeatureFlags();
  posthog.onFeatureFlags(flags => {
    const route = "maintenance-mode";
    if (flags.includes(FEATURE_FLAG.SERVICE_UNAVAILABLE) && to.name !== route) {
      next({ name: route });
    }
  });

  if (to.query.lang) {
    const lang = store.state.settings.languages.find(
      lang => lang.locale === to.query.lang
    );
    if (lang) {
      store.dispatch("settings/changeLanguage", lang);
    }
  }

  const queryCompanyId = getCompanyIdFromQuery(to.query);

  // Authentication must be checked even when company data is already cached.
  if (to.matched.some(record => record.meta.requiresAuth) && !isAuthenticated) {
    next({
      name: "session-login",
      params: { redirectAfterLoginPath: to.fullPath }
    });
    return;
  }

  try {
    if (
      store.state.company.companies.length ||
      !to.meta.requiresAuth ||
      to.matched.some(record => record.path === "/wizard")
    ) {
      if (queryCompanyId && store.state.company.companies.length) {
        await store.dispatch("company/setCompanyById", queryCompanyId);
        ensureCurrentSession(sessionId);
      }
      next();
      return;
    }

    if (queryCompanyId) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.COMPANY_ID, queryCompanyId);
    }

    await fetchCompanies();
    ensureCurrentSession(sessionId);

    if (to.redirectedFrom !== "/wizard") {
      const wizardCompleted = await checkWizardCompleted(next);
      if (!wizardCompleted) return;
      await setLanguage();
      ensureCurrentSession(sessionId);
      await setAnalytics();
    }
    ensureCurrentSession(sessionId);
    next();
  } catch (error) {
    next(axios.isCancel(error) ? false : error);
  }
});

router.beforeResolve((to, from, next) => {
  if (to.meta.hasOwnProperty("module") && !moduleCheck(to.meta.module)) {
    next({ name: "dashboard" });
  } else {
    next();
  }
});

let currentHotjarInstance = null; // 'general' | 'free_tool' | null

router.afterEach((to, from) => {
  Nprogress.done();
  const isFreeTool = to.matched.some(r => r.meta.isFreeTool);
  const targetInstance = isFreeTool ? "free_tool" : "general";

  if (currentHotjarInstance !== targetInstance) {
    stopHotjar();
    const siteId = isFreeTool
      ? process.env.VUE_APP_HOTJAR_FREE_TOOL_SITE_ID
      : process.env.VUE_APP_HOTJAR_SITE_ID;
    initHotjar(siteId);
    currentHotjarInstance = targetInstance;
  }
  if (!store.getters["company/isWizardCompleted"]) return;
  if (
    to.matched.some(record => record.meta.requiresAuth) &&
    !store.state.externalChanges.summary
  ) {
    if (
      (store.getters["company/accountType"] === AccountType.OWNER ||
        store.getters["company/accountType"] === AccountType.ADMIN) &&
      planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA)
    ) {
      store.dispatch("externalChanges/fetchProtectionSummary");
    }
  }
});

Vue.use(GlobalComponents);
Vue.use(GlobalFilters);
Vue.use(Notifications, { velocity });
Vue.use(VueI18n);
Vue.use(VueScrollTo);
Vue.use(VueMatchHeights, {
  disabled: [768]
});
Vue.use(VueGtm, vueGtmConfig);

// custom method registration to omit old (GA Universal) keys added to event (like "category", "action", etc.)
Vue.gtm.trackCustomEvent = params => {
  const { id: company_id, parent: parent_company_id } = store.state.company
    .company || {
    id: null,
    parent: null
  };

  try {
    const isFreeTool = router.currentRoute.matched.some(r => r.meta.isFreeTool);

    if (blockEvents() && !isFreeTool) return;
    let is_paying = null;
    let is_testing = null;
    let is_agency = null;
    let is_free_trial = null;
    let free_trial_end_date = null;
    let free_trial_plan_name = null;
    let company_subscription_type;
    let company_plan;
    let company_acquisition_campaign;

    if (store.state.company.company) {
      is_paying = store.state.company.parent
        ? store.state.company.parent.is_paying
        : store.state.company.company.is_paying;

      is_testing = store.state.company.parent
        ? store.state.company.parent.is_testing
        : store.state.company.company.is_testing;

      is_agency = store.state.company.parent
        ? store.state.company.parent.is_agency
        : store.state.company.company.is_agency;

      is_free_trial = store.getters["auth/isFreeTrial"];
      if (is_free_trial) {
        free_trial_plan_name = store.getters["auth/planName"];
        free_trial_end_date = store.getters["auth/planExpiresAt"];
      }
      const isSubscriptionPlanClient =
        store.getters["company/isSubscriptionPlanClient"];

      company_subscription_type = isSubscriptionPlanClient
        ? "automatic_subscription"
        : "manual_subscription";

      const subsriptionName = store.getters["auth/planName"];

      company_plan = !isSubscriptionPlanClient
        ? "Other"
        : subsriptionName || "No active subscription";

      company_acquisition_campaign =
        store.getters["company/acquisitionCampaign"];
    }

    if (!("event" in params)) throw new Error("[GTM] No event name provided.");

    const eventName = params.event;
    delete params.event;
    const demoUser = localStorage.getItem(LOCAL_STORAGE_KEYS.DEMO_USER);

    const eventData = {
      page_url: router.currentRoute.path,
      page_name: router.currentRoute.name,
      ...(demoUser && { demo_user: demoUser }),
      ...(company_id && { company_id }),
      ...((parent_company_id || company_id) && {
        parent_company_id: parent_company_id || company_id
      }),
      ...params,
      is_paying,
      is_testing,
      is_agency,
      is_free_trial,
      free_trial_plan_name,
      free_trial_end_date,
      company_subscription_type,
      company_plan,
      company_acquisition_campaign
    };

    if (isFreeTool) {
      mixpanel.free_tool.track(eventName, eventData);
    } else {
      mixpanel.core.track(eventName, eventData);
    }

    if ("Intercom" in window) {
      window.Intercom("trackEvent", eventName, eventData);
    }
  } catch (error) {
    dev__warnNotify(error);
  }
};

const currentLang = store.state.settings.selectedLocale;
export const i18n = new VueI18n({
  messages,
  locale: currentLang
});

moment.locale(currentLang);
Vue.use(VeeValidate, {
  i18n,
  dictionary: {
    pl: validationMessagesPl,
    en: validationMessagesEn
  }
});

Sentry.init({
  Vue: Vue,
  integrations: [new Integrations.BrowserTracing()],
  tracingOptions: {
    trackComponents: true
  },
  tracesSampleRate: 1.0,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  environment: process.env.VUE_APP_SENTRY_ENVIRONMENT || "dev"
});

posthog.init(process.env.VUE_APP_POSTHOG_API_KEY || "", {
  api_host: process.env.VUE_APP_POSTHOG_API_HOST || "https://app.posthog.com",
  ui_host: "https://us.posthog.com",
  autocapture: false
});

const app = new Vue({
  store,
  i18n,
  router,
  vuetify,
  render: h => h(App),
  components: { App }
}).$mount("#app");

if (window.Cypress) {
  // only available during E2E tests
  window.app = app;
}

storeSourceFromQuery();
setCustomValidators(i18n);
